import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import css from "./Modal.module.css";

export default function Modal({
    isOpen,
    onClose,
    title,
    children,
    size = "md",
    closeOnClickOutside = true,
    closeOnEsc = true,
    showCloseButton = true,
    className = "",
    centered = false
}) {
    const overlayRef = useRef(null);
    const contentRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => (document.body.style.overflow = prev);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !closeOnEsc) return;
        function onKey(e) {
            if (e.key === "Escape") onClose?.();
        }
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [isOpen, closeOnEsc, onClose]);

    // focus trap
    useEffect(() => {
        if (!isOpen) return;
        const previouslyFocused = document.activeElement;
        const focusable = contentRef.current?.querySelectorAll(
            'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        const toFocus = focusable?.[0] ?? contentRef.current;
        toFocus?.focus?.();

        function handleTab(e) {
            if (e.key !== "Tab") return;
            const nodes = focusable ? Array.from(focusable) : [];
            if (nodes.length === 0) return;
            const first = nodes[0];
            const last = nodes[nodes.length - 1];
            if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            }
        }

        document.addEventListener("keydown", handleTab);
        return () => {
            document.removeEventListener("keydown", handleTab);
            previouslyFocused?.focus?.();
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const sizeClass = {
        sm: css.modalSm,
        md: css.modalMd,
        lg: css.modalLg,
        full: css.modalFull,
    }[size];

    function onOverlayClick(e) {
        if (!closeOnClickOutside) return;
        if (e.target === overlayRef.current) onClose?.();
    }

    return createPortal(
        <div
            className={`${css.modalOverlay} ${centered ? css.centered : css.topPosition}`}
            ref={overlayRef}
            onMouseDown={(e) => {
                e.stopPropagation(); 
                onOverlayClick(e)}
            }
            onClick={(e) => e.stopPropagation()}
            aria-modal="true"
            role="dialog"
        >
            <div
                className={`${css.modal} ${sizeClass} ${className}`}
                ref={contentRef}
                role="document"
                tabIndex={-1}
            >
                {(title || showCloseButton) && (
                    <header className={css.modalHeader}>
                        {title ? <h3 className={css.modalTitle}>{title}</h3> : <div />}

                        {showCloseButton && (
                            <button
                                className={css.modalClose}
                                aria-label="Close dialog"
                                onClick={onClose}
                            >
                                ×
                            </button>
                        )}
                    </header>
                )}

                <div className={css.modalBody}>{children}</div>
            </div>
        </div>,
        document.body
    );
}




//   Usage example:

//   import React, { useState } from 'react';
//   import Modal from './Modal';

//   function App(){
//     const [open, setOpen] = useState(false);
//     return (
//       <div>
//         <button onClick={() => setOpen(true)}>Open</button>
//         <Modal
//           isOpen={open}
//           onClose={() => setOpen(false)}
//           title="My Modal"
//           size="md"
//         >
//           <p>Content goes here</p>
//           <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
//             <button onClick={() => setOpen(false)}>Cancel</button>
//             <button onClick={() => { /* do something */ }}> Save</button >
//           </div >
//         </Modal >
//       </div >
//     )
//   }
